import { useState } from 'react';
import { shallowEqual } from 'react-redux';

import { useQueryClient } from '@zg-rentals/react-query5';
import { ProofRecomendedActions } from '@zg-rentals/renters-insurance-c10';
import { ModalTenantSelect } from '@zg-rentals/modal-tenant';
import { logError } from '@zg-rentals/log-error';
import { useRentalManagerRenterContactWebApiHandler_getTenants } from '@zg-rentals/rental-manager-env';

import { useSelector } from '../../../selectors/useSelector';
import { createZgApiHttpClient } from '../../../../client/zgApiHttpClient';

import TenantDirectoryCSEvents from '../../../model/ClickstreamEvent/TenantDirectoryCSEvents';
import { Toast, useToast } from '@zillow/constellation-10';

type ProofOfInsuranceModalWrapperProps = {
  listingAlias: string;
  onClose: () => void;
  onSendRequestClick: () => void;
};

const ProofOfInsuranceModalWrapper = ({
  listingAlias,
  onClose,
  onSendRequestClick,
}: ProofOfInsuranceModalWrapperProps) => {
  const { enqueueToast } = useToast();
  const queryClient = useQueryClient();
  const [isModalTenantSelectOpen, setIsModalTenantSelectOpen] = useState(false);
  const [isProofRecomendedActionsOpen, setIsProofRecomendedActionsOpen] = useState(true);

  const { currentProperty, user, env } = useSelector(
    (state) => ({
      currentProperty: state.properties.currentProperty,
      user: state.user,
      env: state.env,
    }),
    shallowEqual,
  );

  // using same client as in TenantsModule
  const client = createZgApiHttpClient(env.zgApiPublic);
  const invalidateGetTenantsQuery = () => {
    queryClient.invalidateQueries({
      queryKey: useRentalManagerRenterContactWebApiHandler_getTenants.getKey({ listingAlias }),
    });
  };

  const handleAddTenantsClick = () => {
    setIsModalTenantSelectOpen(true);
    setIsProofRecomendedActionsOpen(false);
  };

  const handleOnModalClose = () => {
    setIsModalTenantSelectOpen(false);
    setIsProofRecomendedActionsOpen(false);
    onClose();
  };

  const handleSaveTenantsClick = () => {
    invalidateGetTenantsQuery();
    setIsModalTenantSelectOpen(false);
    setIsProofRecomendedActionsOpen(true);
  };

  const handleRequestProofSuccess = (message: string) => {
    invalidateGetTenantsQuery();
    enqueueToast(<Toast tone="success">{message}</Toast>);
  };

  const handleRequestProofError = (message: string) => {
    invalidateGetTenantsQuery();
    enqueueToast(<Toast tone="critical">{message}</Toast>);
  };

  return (
    <>
      <ProofRecomendedActions
        listingAlias={listingAlias}
        isOpen={isProofRecomendedActionsOpen}
        onSendRequestClick={onSendRequestClick}
        onAddTenantsClick={handleAddTenantsClick}
        onSendRequestEmailSuccess={handleRequestProofSuccess}
        onSendRequestEmailError={handleRequestProofError}
        onClose={handleOnModalClose}
      />
      <ModalTenantSelect
        client={client}
        listingAlias={listingAlias}
        isOpen={isModalTenantSelectOpen}
        onAddRenterSuccess={() => {
          if (currentProperty && user) {
            TenantDirectoryCSEvents.TenantsModuleSaveClick({
              triggerLocation: TenantDirectoryCSEvents.CS_EVENT_TRIGGER_LOCATION.ZRM_POI_ADD_TENANTS_MODAL,
              currentProperty,
              user,
            });
          } else {
            logError({
              error: new Error(
                'Unable to fire Clickstream event_type_id: ' +
                  TenantDirectoryCSEvents.TenantsModuleSaveClick.data_event_id,
              ),
              errorType: 'AnalyticsError',
              context: {
                triggerLocation: TenantDirectoryCSEvents.CS_EVENT_TRIGGER_LOCATION.ZRM_POI_ADD_TENANTS_MODAL,
                currentProperty,
                user,
              },
            });
          }
        }}
        onAddRenterError={handleOnModalClose}
        queryClient={queryClient}
        onClose={handleOnModalClose}
        onPrimaryCTAClick={handleSaveTenantsClick}
        closeOnSuccess={false}
      />
    </>
  );
};

export default ProofOfInsuranceModalWrapper;
